import React from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button } from 'antd'
import { Header } from 'antd/es/layout/layout'
import { DownloadOutlined, LoginOutlined } from '@ant-design/icons'
import fetchDocumentToViewer from 'store/reducers/viewerCreator'
import { useAppDispatch, useAppSelector } from 'store/hook'
import UploadingComponent from 'components/UploadingComponent/UploadingComponent'
import PDFViewer from 'components/Webviewer/Viewer/PDFViewer'
import getFilenameFromResponse from 'helpers/getFilenameFromResponse'
import ErrorPage from './ErrorPage'
import ShareBrowserModal from './ShareBrowserModal'
import PasswordModal from './PasswordModal'
import convert from 'api/convert'
import classes from './TempLink.module.scss'
import ImageViewer from 'components/Webviewer/ImageViewer/ImageViewer'
import XLSXViewer from 'components/Webviewer/XLSXViewer/XLSXViewer'
import VideoViewer from 'components/Webviewer/VideoViewer/VideoViewer'
import cn from 'classnames'
import api from 'api'
import WorkerComponent from 'components/SessionComponent/WorkerComponent'
import SessionComponent from 'components/SessionComponent/SessionComponent'
import { resetViewerSlice, setViewerSlice } from 'store/slices/viewerSlice'
import { convertableTypesFiles, imageExtensions, videoExtensions } from 'helpers/filesExtensions'
import EditTab from 'components/DetailsComponent/EditTab/EditTab'
import { setSelectedDocument } from 'store/slices/dataDocumentsSlice'

const buildName = window.REACT_APP_BUILD_NAME !== 'REPLACE_REACT_APP_BUILD_NAME' ? window.REACT_APP_BUILD_NAME : process.env.REACT_APP_BUILD_NAME || 'tazadeal';
const isAvailableEditing = window.REACT_APP_IS_EDITING_ACTIVE !== 'REPLACE_REACT_APP_IS_EDITING_ACTIVE' ? window.REACT_APP_IS_EDITING_ACTIVE : process.env.REACT_APP_IS_EDITING_ACTIVE || 'false';
const mainViewer = window.REACT_APP_TEMP_MAIN_VIEWER !== 'REPLACE_REACT_APP_TEMP_MAIN_VIEWER' ? window.REACT_APP_TEMP_MAIN_VIEWER : process.env.REACT_APP_TEMP_MAIN_VIEWER || '';
const authType = window.REACT_APP_AUTH_TYPE !== 'REPLACE_REACT_APP_AUTH_TYPE' ? window.REACT_APP_AUTH_TYPE : process.env.REACT_APP_AUTH_TYPE; 
const loginAdress = authType === 'blitz' ? 'blitz-auth' : 'login';

export default function TempLink() {
  const [isDownloadingDocument, setIsDownloadingDocument] = React.useState(false);
  const [isOpenShareModal, setIsOpenShareModal] = React.useState(false);
  const [isSharedInfo, setIsSharedInfo] = React.useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = React.useState(false);
  const [isPasswordVerifying, setIsPasswordVerifying] = React.useState(false);
  const [documentPassword, setDocumentPassword] = React.useState<string>('');
  const { documentUrl, documentName, isDocumentLoading, isDocumentError } = useAppSelector(state => state.viewer);
  const { messageApi } = useAppSelector((store) => store.windowState);  

  const logoTitle = buildName === 'athena' ? 'Athena' : 'TazaDeal';

  const isMobile = /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(navigator.userAgent)

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { document_token } = useParams();
  const [searchParams] = useSearchParams();
  const agreeToShareIpAndBrowser = localStorage.getItem('agreeToShareIpAndBrowser');

  const documentExtension = searchParams.get('ext') || '';
  const documentPermissions = searchParams.get('permissions')?.split(',');
  const downloadAvailability = documentUrl && documentPermissions?.includes('can_download') && documentPermissions?.includes('can_download_pdf') && !isDocumentError; // !== 'TEMPLINK_IS_EXPIRED';
  const isPasswordRequired = searchParams.get('password_active') === 'true';
  const possibleUseCollaboraAsViewer = isAvailableEditing === 'true'
    && ((mainViewer === 'collabora' && [...convertableTypesFiles, 'pdf'].includes(documentExtension)) || (documentExtension === 'xlsx'));
    
  React.useEffect(() => {
    setIsSharedInfo(Boolean(agreeToShareIpAndBrowser));
    !agreeToShareIpAndBrowser && setIsOpenShareModal(true);
    return () => {
      dispatch(resetViewerSlice())
    }
  }, []);

  React.useEffect(() => {
    if (isSharedInfo && isPasswordRequired && !documentPassword) {
      setIsPasswordModalOpen(true);
    }
  }, [isSharedInfo, isPasswordRequired, documentPassword]);

  React.useEffect(() => {
    !possibleUseCollaboraAsViewer && incrementCounter(Boolean(documentUrl));
  }, [documentUrl]);

  React.useEffect(() => {
    const canFetchDocument = isSharedInfo && document_token && documentExtension && !isPasswordRequired;    
    if (canFetchDocument && !possibleUseCollaboraAsViewer) {
      fetchDocument(documentExtension)
    }
  }, [document_token, isSharedInfo, searchParams]);

  React.useEffect(() => {
    if (possibleUseCollaboraAsViewer && isSharedInfo && document_token && !isPasswordRequired) {
      dispatch(setSelectedDocument({id: document_token, token: document_token, name: '', extension: documentExtension}));
      dispatch(setViewerSlice({fileURL: document_token, extension: documentExtension, filename: ''}))
    }
  }, [possibleUseCollaboraAsViewer, isSharedInfo]);
  
  const fetchDocument = async (extension: string, password?: string) => {
    try {
      const result = await dispatch(
        fetchDocumentToViewer({
          action: 'view',
          token: document_token!,
          is_agreement: false,
          is_temp_links: true,
          extension,
          locale: i18n.language,
          ...(password && { password }),
        })
      ).unwrap();
      return result;
    } catch (error: any) {
      if (error === 'TEMPLINK_IS_EXPIRED') {
        messageApi?.error(t('templink.linkExpired'));
      } else if (error === 'SERVER_ERROR') {
        messageApi?.error(t('templink.passwordModal.serverError'));
      } else if (error === 'WRONG_PASSWORD') {
        messageApi?.error(t('templink.passwordModal.wrongPassword'));
      } else if (typeof error === 'string' && error.length > 0) {
        messageApi?.error(error);
      } else {
        messageApi?.error(t('templink.documentError'));
      }
    }
  };

  const incrementCounter = async (needToIncremen: boolean) => {
    needToIncremen && await api.incrementCounter(document_token!);
  }

  const downloadDocument = async () => {
    setIsDownloadingDocument(true)
    try {
      let response: any;
      const payload = {
        action: 'download',
        token: document_token!,
        is_agreement: false,
        is_temp_links: true,
        locale: i18n.language,
        ...(documentPassword && { password: documentPassword }),
      }
      
      if ([...imageExtensions, ...videoExtensions, 'xlsx'].includes(documentExtension!)) {
        response = await api.downloadFile({ ...payload, is_webview: true, extension: documentExtension });
      } else {
        response = await convert.convertDocument(payload);
      }
     
      const filename = getFilenameFromResponse(response);

      const url = window.URL.createObjectURL(response.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'temp_document.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      messageApi?.error(t('templink.errorDownload'), 5);
    } finally {
      setIsDownloadingDocument(false);
    }
  }

  const toLogin = () => isMobile ? navigate('/unsupported') : navigate(`/${loginAdress}`);
  const closeShareModal = () => {
    setIsOpenShareModal(false);
    toLogin();
  };
  const recordConsent = () => {
    setIsOpenShareModal(false);
    localStorage.setItem('agreeToShareIpAndBrowser', 'true');
    setIsSharedInfo(true);
  };

  const handlePasswordSubmit = async (password: string) => {
    setIsPasswordVerifying(true);
    try {
      if (possibleUseCollaboraAsViewer && document_token && documentExtension) {
        setDocumentPassword(password);
        dispatch(setSelectedDocument({id: document_token!, token: document_token, name: '', extension: documentExtension}));
        dispatch(setViewerSlice({fileURL: document_token, extension: documentExtension, filename: ''}));
        setIsPasswordModalOpen(false);
      } else {
        const result = await fetchDocument(documentExtension, password);
        if (result && result.fileURL) {
          setDocumentPassword(password);
          setIsPasswordModalOpen(false);
        }
      }
    } catch (error: any) {
    } finally {
      setIsPasswordVerifying(false);
    }
  };

  const handlePasswordCancel = () => {
    navigate('/');
  };

  const chooseViewer = () => {
    let viewer = '';
    if (imageExtensions.includes(documentExtension)) {
      viewer = 'image';
    } else if (videoExtensions.includes(documentExtension)) {
      viewer = 'video';
    } else if (possibleUseCollaboraAsViewer) {
      viewer = 'collabora'
    } else if (documentExtension === 'xlsx') {
      viewer = 'xlsx';
    } 
    
    switch(viewer) {
      case 'xlsx':
        return <XLSXViewer data={documentUrl} downloadDocument={downloadAvailability ? downloadDocument : undefined}/>;
      case 'image':
        return <ImageViewer url={documentUrl} downloadDocument={downloadAvailability ? downloadDocument : undefined}/>;
      case 'video':
        return <VideoViewer url={documentUrl} downloadDocument={downloadAvailability ? downloadDocument : undefined}/>;
      case 'collabora':
        return <EditTab is_temp_link={true} password={documentPassword} is_download={downloadAvailability}/>
      default: 
        return <PDFViewer url={documentUrl} isModal={false} downloadDocument={downloadAvailability ? downloadDocument : undefined} isMobile={isMobile}/>;
    };
  };

  return (
    <div className={classes.viewerWrap}>
      <Header className={classes.headerWrapper}>
        {!isMobile && <div className={cn(classes.title, isMobile ? classes.mobTitle : 'null' )}>
          {logoTitle}
        </div>}

        <div className={cn(classes.filename, isMobile ? classes.mobWidth : 'null')}>
          <div style={{fontWeight: 600}}>{t('templink.viewdocument')}</div>
          {document_token &&
            <div>
              { downloadAvailability &&
                <Button size='small' onClick={downloadDocument} className={classes.downloadBtn} icon={<DownloadOutlined />}>
                  {t('templink.download')}
                </Button>
              }
              {documentName} 
            </div>}
        </div>

        <Button
          onClick={toLogin}
          type='text'
          className={classes.enterBtn}
          icon={<LoginOutlined style={{fontSize: 20}} />}
          size={isMobile ? 'small' : 'large'}
        >
          {isMobile ? '' : t('login.logIn')}
        </Button>
      </Header>

      <div className={cn(classes.viewerBody, imageExtensions.includes(documentExtension) ? classes.flex : null)}>
        {(isDocumentLoading || !documentUrl)
          ? (
            isDocumentError
              ? <ErrorPage error={isDocumentError}/>
              : ( agreeToShareIpAndBrowser === 'false' && <UploadingComponent /> )
          )
          : chooseViewer()
        }
      </div>

      {
        isDownloadingDocument && <UploadingComponent />
      }

      <React.Suspense fallback={<div />}>
        {isOpenShareModal && (
          <ShareBrowserModal open={isOpenShareModal} onCancel={closeShareModal} recordConsent={recordConsent}/>
        )}
        {isPasswordModalOpen && (
          <PasswordModal 
            open={isPasswordModalOpen} 
            onSubmit={handlePasswordSubmit}
            onCancel={handlePasswordCancel}
            loading={isPasswordVerifying}
          />
        )}
      </React.Suspense>

      {window.SharedWorker ? <WorkerComponent /> : <SessionComponent />}

    </div>
  );
}
